/**
 * Runtime Syncfusion theme + density (size mode) manager.
 *
 * Syncfusion themes are plain CSS files that can only be swapped by changing
 * the `href` of `<link>` elements — there is no JS theming API for EJ2. This
 * module keeps one managed `<link>` tag per Syncfusion package that ships
 * CSS in `<head>` and repoints them all to the selected theme.
 *
 * Density uses Syncfusion's native, built-in size-mode classes
 * (`e-small` / default / `e-bigger`), which every theme is compiled with.
 * No custom CSS overrides are used.
 */
import materialBase from '@syncfusion/ej2-base/styles/material.css?url';
import materialGrids from '@syncfusion/ej2-react-grids/styles/material.css?url';
import materialNav from '@syncfusion/ej2-react-navigations/styles/material.css?url';
import materialPopups from '@syncfusion/ej2-react-popups/styles/material.css?url';
import materialNotifications from '@syncfusion/ej2-react-notifications/styles/material.css?url';
import materialDiagrams from '@syncfusion/ej2-react-diagrams/styles/material.css?url';

import material3Base from '@syncfusion/ej2-base/styles/material3.css?url';
import material3Grids from '@syncfusion/ej2-react-grids/styles/material3.css?url';
import material3Nav from '@syncfusion/ej2-react-navigations/styles/material3.css?url';
import material3Popups from '@syncfusion/ej2-react-popups/styles/material3.css?url';
import material3Notifications from '@syncfusion/ej2-react-notifications/styles/material3.css?url';
import material3Diagrams from '@syncfusion/ej2-react-diagrams/styles/material3.css?url';

import bootstrap5Base from '@syncfusion/ej2-base/styles/bootstrap5.css?url';
import bootstrap5Grids from '@syncfusion/ej2-react-grids/styles/bootstrap5.css?url';
import bootstrap5Nav from '@syncfusion/ej2-react-navigations/styles/bootstrap5.css?url';
import bootstrap5Popups from '@syncfusion/ej2-react-popups/styles/bootstrap5.css?url';
import bootstrap5Notifications from '@syncfusion/ej2-react-notifications/styles/bootstrap5.css?url';
import bootstrap5Diagrams from '@syncfusion/ej2-react-diagrams/styles/bootstrap5.css?url';

import bootstrap53Base from '@syncfusion/ej2-base/styles/bootstrap5.3.css?url';
import bootstrap53Grids from '@syncfusion/ej2-react-grids/styles/bootstrap5.3.css?url';
import bootstrap53Nav from '@syncfusion/ej2-react-navigations/styles/bootstrap5.3.css?url';
import bootstrap53Popups from '@syncfusion/ej2-react-popups/styles/bootstrap5.3.css?url';
import bootstrap53Notifications from '@syncfusion/ej2-react-notifications/styles/bootstrap5.3.css?url';
import bootstrap53Diagrams from '@syncfusion/ej2-react-diagrams/styles/bootstrap5.3.css?url';

import tailwindBase from '@syncfusion/ej2-base/styles/tailwind.css?url';
import tailwindGrids from '@syncfusion/ej2-react-grids/styles/tailwind.css?url';
import tailwindNav from '@syncfusion/ej2-react-navigations/styles/tailwind.css?url';
import tailwindPopups from '@syncfusion/ej2-react-popups/styles/tailwind.css?url';
import tailwindNotifications from '@syncfusion/ej2-react-notifications/styles/tailwind.css?url';
import tailwindDiagrams from '@syncfusion/ej2-react-diagrams/styles/tailwind.css?url';

import tailwind3Base from '@syncfusion/ej2-base/styles/tailwind3.css?url';
import tailwind3Grids from '@syncfusion/ej2-react-grids/styles/tailwind3.css?url';
import tailwind3Nav from '@syncfusion/ej2-react-navigations/styles/tailwind3.css?url';
import tailwind3Popups from '@syncfusion/ej2-react-popups/styles/tailwind3.css?url';
import tailwind3Notifications from '@syncfusion/ej2-react-notifications/styles/tailwind3.css?url';
import tailwind3Diagrams from '@syncfusion/ej2-react-diagrams/styles/tailwind3.css?url';

import fluentBase from '@syncfusion/ej2-base/styles/fluent.css?url';
import fluentGrids from '@syncfusion/ej2-react-grids/styles/fluent.css?url';
import fluentNav from '@syncfusion/ej2-react-navigations/styles/fluent.css?url';
import fluentPopups from '@syncfusion/ej2-react-popups/styles/fluent.css?url';
import fluentNotifications from '@syncfusion/ej2-react-notifications/styles/fluent.css?url';
import fluentDiagrams from '@syncfusion/ej2-react-diagrams/styles/fluent.css?url';

import fluent2Base from '@syncfusion/ej2-base/styles/fluent2.css?url';
import fluent2Grids from '@syncfusion/ej2-react-grids/styles/fluent2.css?url';
import fluent2Nav from '@syncfusion/ej2-react-navigations/styles/fluent2.css?url';
import fluent2Popups from '@syncfusion/ej2-react-popups/styles/fluent2.css?url';
import fluent2Notifications from '@syncfusion/ej2-react-notifications/styles/fluent2.css?url';
import fluent2Diagrams from '@syncfusion/ej2-react-diagrams/styles/fluent2.css?url';

import highContrastBase from '@syncfusion/ej2-base/styles/highcontrast.css?url';
import highContrastGrids from '@syncfusion/ej2-react-grids/styles/highcontrast.css?url';
import highContrastNav from '@syncfusion/ej2-react-navigations/styles/highcontrast.css?url';
import highContrastPopups from '@syncfusion/ej2-react-popups/styles/highcontrast.css?url';
import highContrastNotifications from '@syncfusion/ej2-react-notifications/styles/highcontrast.css?url';
import highContrastDiagrams from '@syncfusion/ej2-react-diagrams/styles/highcontrast.css?url';

export type SyncfusionThemeId =
  | 'material'
  | 'material3'
  | 'bootstrap5'
  | 'bootstrap5.3'
  | 'tailwind'
  | 'tailwind3'
  | 'fluent'
  | 'fluent2'
  | 'highcontrast';

export type SyncfusionDensityMode = 'small' | 'normal' | 'touch';

interface SyncfusionThemeDefinition {
  id: SyncfusionThemeId;
  label: string;
  baseHref: string;
  gridsHref: string;
  navigationsHref: string;
  popupsHref: string;
  notificationsHref: string;
  diagramsHref: string;
}

export const SYNCFUSION_THEMES: SyncfusionThemeDefinition[] = [
  {
    id: 'material',
    label: 'Material',
    baseHref: materialBase,
    gridsHref: materialGrids,
    navigationsHref: materialNav,
    popupsHref: materialPopups,
    notificationsHref: materialNotifications,
    diagramsHref: materialDiagrams,
  },
  {
    id: 'material3',
    label: 'Material 3',
    baseHref: material3Base,
    gridsHref: material3Grids,
    navigationsHref: material3Nav,
    popupsHref: material3Popups,
    notificationsHref: material3Notifications,
    diagramsHref: material3Diagrams,
  },
  {
    id: 'bootstrap5',
    label: 'Bootstrap 5',
    baseHref: bootstrap5Base,
    gridsHref: bootstrap5Grids,
    navigationsHref: bootstrap5Nav,
    popupsHref: bootstrap5Popups,
    notificationsHref: bootstrap5Notifications,
    diagramsHref: bootstrap5Diagrams,
  },
  {
    id: 'bootstrap5.3',
    label: 'Bootstrap 5.3',
    baseHref: bootstrap53Base,
    gridsHref: bootstrap53Grids,
    navigationsHref: bootstrap53Nav,
    popupsHref: bootstrap53Popups,
    notificationsHref: bootstrap53Notifications,
    diagramsHref: bootstrap53Diagrams,
  },
  {
    id: 'tailwind',
    label: 'Tailwind',
    baseHref: tailwindBase,
    gridsHref: tailwindGrids,
    navigationsHref: tailwindNav,
    popupsHref: tailwindPopups,
    notificationsHref: tailwindNotifications,
    diagramsHref: tailwindDiagrams,
  },
  {
    id: 'tailwind3',
    label: 'Tailwind 3',
    baseHref: tailwind3Base,
    gridsHref: tailwind3Grids,
    navigationsHref: tailwind3Nav,
    popupsHref: tailwind3Popups,
    notificationsHref: tailwind3Notifications,
    diagramsHref: tailwind3Diagrams,
  },
  {
    id: 'fluent',
    label: 'Fluent',
    baseHref: fluentBase,
    gridsHref: fluentGrids,
    navigationsHref: fluentNav,
    popupsHref: fluentPopups,
    notificationsHref: fluentNotifications,
    diagramsHref: fluentDiagrams,
  },
  {
    id: 'fluent2',
    label: 'Fluent 2',
    baseHref: fluent2Base,
    gridsHref: fluent2Grids,
    navigationsHref: fluent2Nav,
    popupsHref: fluent2Popups,
    notificationsHref: fluent2Notifications,
    diagramsHref: fluent2Diagrams,
  },
  {
    id: 'highcontrast',
    label: 'High Contrast',
    baseHref: highContrastBase,
    gridsHref: highContrastGrids,
    navigationsHref: highContrastNav,
    popupsHref: highContrastPopups,
    notificationsHref: highContrastNotifications,
    diagramsHref: highContrastDiagrams,
  },
];

interface SyncfusionDensityDefinition {
  id: SyncfusionDensityMode;
  label: string;
  /** Native Syncfusion size-mode class, applied to `<body>`. Empty for the default (normal) density. */
  bodyClass: string;
}

export const SYNCFUSION_DENSITY_MODES: SyncfusionDensityDefinition[] = [
  { id: 'small', label: 'Small (compact)', bodyClass: 'e-small' },
  { id: 'normal', label: 'Normal (default)', bodyClass: '' },
  { id: 'touch', label: 'Touch (bigger)', bodyClass: 'e-bigger' },
];

const DEFAULT_THEME: SyncfusionThemeId = 'material';
const DEFAULT_DENSITY: SyncfusionDensityMode = 'normal';

const THEME_STORAGE_KEY = 'ledgervue.syncfusionTheme';
const DENSITY_STORAGE_KEY = 'ledgervue.syncfusionDensity';

const LINK_IDS = {
  base: 'syncfusion-theme-base',
  grids: 'syncfusion-theme-grids',
  navigations: 'syncfusion-theme-navigations',
  popups: 'syncfusion-theme-popups',
  notifications: 'syncfusion-theme-notifications',
  diagrams: 'syncfusion-theme-diagrams',
} as const;

function findTheme(id: SyncfusionThemeId): SyncfusionThemeDefinition {
  return SYNCFUSION_THEMES.find((theme) => theme.id === id) ?? SYNCFUSION_THEMES[0];
}

function findDensity(id: SyncfusionDensityMode): SyncfusionDensityDefinition {
  return SYNCFUSION_DENSITY_MODES.find((density) => density.id === id) ?? SYNCFUSION_DENSITY_MODES[1];
}

function ensureLinkElement(id: string): HTMLLinkElement {
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  return link;
}

export function getStoredSyncfusionTheme(): SyncfusionThemeId {
  const stored = localStorage.getItem(THEME_STORAGE_KEY) as SyncfusionThemeId | null;
  return stored && SYNCFUSION_THEMES.some((theme) => theme.id === stored) ? stored : DEFAULT_THEME;
}

export function getStoredSyncfusionDensity(): SyncfusionDensityMode {
  const stored = localStorage.getItem(DENSITY_STORAGE_KEY) as SyncfusionDensityMode | null;
  return stored && SYNCFUSION_DENSITY_MODES.some((density) => density.id === stored) ? stored : DEFAULT_DENSITY;
}

export function applySyncfusionTheme(id: SyncfusionThemeId): void {
  const theme = findTheme(id);
  ensureLinkElement(LINK_IDS.base).href = theme.baseHref;
  ensureLinkElement(LINK_IDS.grids).href = theme.gridsHref;
  ensureLinkElement(LINK_IDS.navigations).href = theme.navigationsHref;
  ensureLinkElement(LINK_IDS.popups).href = theme.popupsHref;
  ensureLinkElement(LINK_IDS.notifications).href = theme.notificationsHref;
  ensureLinkElement(LINK_IDS.diagrams).href = theme.diagramsHref;
  localStorage.setItem(THEME_STORAGE_KEY, id);
}

export function applySyncfusionDensity(id: SyncfusionDensityMode): void {
  const allBodyClasses = SYNCFUSION_DENSITY_MODES.map((density) => density.bodyClass).filter(Boolean);
  document.body.classList.remove(...allBodyClasses);

  const density = findDensity(id);
  if (density.bodyClass) {
    document.body.classList.add(density.bodyClass);
  }
  localStorage.setItem(DENSITY_STORAGE_KEY, id);
}

/** Applies the persisted (or default) theme and density. Call once at app startup. */
export function initializeSyncfusionAppearance(): void {
  applySyncfusionTheme(getStoredSyncfusionTheme());
  applySyncfusionDensity(getStoredSyncfusionDensity());
}
